import React, {ReactElement, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import HTMLReactParserSafe from '../../partials/html-react-parser-safe';
import {PaginatedResponse} from "../../../structures/paginatedResponse";
import {APP_URL, BROWSER_API_PREFIX, LP_API} from "../../../utils/constants";
import StarRating from "../../buttons/star-rating";
import ReadMore from "../../buttons/ReadMore";
import DateHelper from "../../../utils/DateHelper";
import moment from "moment";

interface SellerReviewsProps {
  sellerId: string
  firstPage: string
}

export interface Review {
  id: number;
  authorName: string;
  seller_id: number;
  message: string;
  rating: number;
  created_at: string;
  updated_at: string;
}

interface SellerReviewsResponse extends PaginatedResponse {
  data: Review[]
}

const SellerReviews: React.FC<SellerReviewsProps> = ({sellerId, firstPage}): ReactElement => {
  const [reviews, setReviews] = useState<SellerReviewsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Replace the remote API URL with the local browser API URL
   *
   * @param url
   */
  const mapRemoteUrlToLocal = (url: string) => {
    return url.replace(LP_API, APP_URL + BROWSER_API_PREFIX);
  }

  /**
   * Load reviews from the browser API
   *
   * @param page
   */
  const loadReviews = (page: string | null) => {
    axios.get(mapRemoteUrlToLocal(page ?? firstPage))
      .then(({data}) => {
        setReviews(data);
        setError(null);
      })
      .catch(() => {
        setReviews(null);
        setError('An error occurred while fetching reviews');
      });
  };

  useEffect(() => {
    loadReviews(null);
  }, []);

  if (error) {
    return <div>{error}</div>;
  }

  if (!reviews) {
    return <div>Loading...</div>;
  }

  return (
    <div className="reviews-container">
      <div>
        {
          reviews.data.map((review, index) => (
              <div className="seller-review-card py-3" key={review.id}>
                <div className="row m-0">
                  <div className="col-md-3">
                    <div className="d-table w-100">
                      <div className="d-table-cell w-100 align-top review-user">
                        <p>
                          <strong>{review.authorName}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-9">
                    <div className="row">
                    </div>
                    <div className="row">
                      <div className="col-12 review-txt">
                        <div className="d-flex justify-content-between">
                          <StarRating rating={review.rating} />
                          <div>
                            <span>{DateHelper.getLocalizedDateTime(moment(review.created_at)).format('M/D/Y')}</span>
                          </div>
                        </div>
                        <ReadMore text={review.message} />
                      </div>
                    </div>
                  </div>
                </div>
              {index < reviews.data.length - 1 && <hr />}
              </div>
          ))
        }
      </div>
      {
        reviews.meta.last_page > 1 && (
          <div className="reviews-pagination">
            <nav>
              <ul className="pagination">
                {
                  reviews.meta.links.map((link) => {
                    const isDisabled = link.label === '...' || link.active || !link.url;
                    return (
                      <li
                        key={link.label + link.url}
                        className={`page-item ${link.active ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                      >
                        <a
                          className={`page-link ${link.active ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            loadReviews(link.url ?? '');
                            const reviewsTop = document.getElementById('seller-reviews');
                            if (reviewsTop) {
                              reviewsTop.scrollIntoView({behavior: 'smooth', block: 'start'});
                            }
                          }}
                        >
                          <HTMLReactParserSafe html={link.label} />
                        </a>
                      </li>
                    );
                  })
                }
              </ul>
            </nav>
          </div>
        )
      }
    </div>
  );
};

if (document.getElementById('seller-reviews')) {
  const element = document.getElementById('seller-reviews') as HTMLElement;
  const sellerId = element.getAttribute('data-seller-id') as string;
  const firstPage = element.getAttribute('data-first-page') as string;
  ReactDOM.render(
    <SellerReviews sellerId={sellerId} firstPage={firstPage} />,
    element,
  );
}
